/* eslint-disable import/max-dependencies */
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import type { Dispatch } from "redux";
import {
  Container, Box, Toolbar, Card,
} from "@mui/material";
import { SetupCard, Loader } from "@fas/ui-core";
import debounce from "lodash.debounce";
import { getErrors } from "@fas/ui-framework";
import CodeEditor from "../../components/CodeEditor";
import CodeEditorActionButtons from "../../components/CodeEditorActions";
import {
  getPageType,
  getPageCurrentVersionType,
  getPageVersionByType,
  getDocumentModifiers,
  getPageResources,
  getDefaultModifiersName,
  getPageInfo,
} from "../../selectors/page";
import {
  removeModifier,
  setModifierByName,
} from "../../actions/pageModifiers";
import {
  changePageVersionHtml,
  changePageVersionCss,
  changePageVersionJs,
} from "../../actions/pageVersions";
import {
  addPageResource,
  removePageResource,
  removeAllPageResource,
  downloadPageResourcesSaga,
} from "../../actions/pageResources";
import { getPageSaga } from "../../actions/page";
import { pageBackupStore } from "../../pages/AppStoreWrapper/store";
import { setBackup, restoreBackup } from "../../actions/backup";
import PageModifiers from "../../components/PageModifiers";
import PageSettingsResources from "../../components/PageSettingsResources";

import type { State as PageStoreState } from "../../pages/AppStoreWrapper";
import type { Version, VersionType } from "../../reducers/pageVersions";

import { getResourceTypes } from "../../selectors/accountModules";
import {
  getIsPageLoading,
  getIsResourceDownloading,
} from "../../selectors/loading";
import { type Modifier, DefaultState as defaultModifiers } from "../../reducers/pageModifiers/reducer";
import ModifierFormContainer from "../ModifierForm";
import type { StateToProps, DispatchToProps, Props } from "./types/PageSettings.type";

const mapStateToProps: (state: PageStoreState) => StateToProps = (state) => {
  const versionType: VersionType = getPageCurrentVersionType(state);
  const pageVersion: Version = getPageVersionByType(state, versionType);
  const pageInfo = getPageInfo(state);

  return {
    pageModifiers: getDocumentModifiers(state),
    defaultPageModifiersName: getDefaultModifiersName(),
    pageResources: getPageResources(state),
    errors: getErrors(state),
    pageType: getPageType(state),
    versionType,
    pageVersion,
    pageInfo,
    fileFormats: getResourceTypes(state),
    isPageLoading: getIsPageLoading(state),
    isResourceDownloading: getIsResourceDownloading(state),
  };
};

const mapDispatchToProps: (arg0: Dispatch<any>) => DispatchToProps = dispatch => ({
  ...bindActionCreators({
    onSetModifierByName: setModifierByName,
    onChangePageVersionHtml: changePageVersionHtml,
    onChangePageVersionCss: changePageVersionCss,
    onChangePageVersionJs: changePageVersionJs,
    onAddDocumentResource: addPageResource,
    onRemoveDocumentResource: removePageResource,
    onRemoveAllDocumentResource: removeAllPageResource,
    onRemoveModifier: removeModifier,
    onDownloadPageResources: downloadPageResourcesSaga,
    onGetPage: getPageSaga,
    onSetBackup: setBackup,
    onRestoreBackup: restoreBackup,
  }, dispatch),
});

function PageSettingsContainer({
  pageModifiers,
  defaultPageModifiersName,
  pageResources,
  errors,
  onSetModifierByName,
  onChangePageVersionHtml,
  onChangePageVersionCss,
  onChangePageVersionJs,
  onAddDocumentResource,
  onRemoveDocumentResource,
  onRemoveAllDocumentResource,
  onDownloadPageResources,
  pageType,
  versionType,
  pageVersion,
  fileFormats,
  isPageLoading,
  isResourceDownloading,
  onRemoveModifier,
  onGetPage,
  onSetBackup,
  onRestoreBackup,
  pageInfo,
}: Props) {
  const {
    html,
    css,
    js,
  } = pageVersion;

  const location = useLocation();

  const { id } = useParams<{ id?: string }>();
  const searchParams: URLSearchParams = new URLSearchParams(location.search);
  const versionId: string | null = searchParams.get("versionId");

  useEffect(() => {
    onSetBackup(pageBackupStore);
    if (id) onGetPage(id, versionId);
    return () => {
      onRestoreBackup();
    };
  }, [id, onGetPage, versionId]);

  const wait: number = 600;
  const debouncedOnChangeDocumentHtml = useCallback(debounce(onChangePageVersionHtml, wait), [onChangePageVersionHtml]);
  const debouncedOnChangeDocumentCss = useCallback(debounce(onChangePageVersionCss, wait), [onChangePageVersionCss]);
  const debouncedOnChangeDocumentJs = useCallback(debounce(onChangePageVersionJs, wait), [onChangePageVersionJs]);
  const [modifiersToRender, setModifiers] = useState<Array<Modifier>>([]);

  useEffect(() => {
    if (pageType === "template") {
      setModifiers(pageModifiers.slice(defaultModifiers.allIds.size));
    }
    else {
      setModifiers(pageModifiers);
    }
  }, [pageType, pageModifiers]);

  const handleHtmlEditorChange = (value: string) => {
    debouncedOnChangeDocumentHtml(versionType, value);
  };

  const handleCssEditorChange = (value: string) => {
    debouncedOnChangeDocumentCss(versionType, value);
  };

  const handleJsEditorChange = (value: string) => {
    debouncedOnChangeDocumentJs(versionType, value);
  };

  return (
    <Container maxWidth="xl">
      <Loader loading={isPageLoading}>
        <Box display="flex" flexDirection={pageType === "pft" ? "column-reverse" : "column"}>
          <Box>
            {
              pageType !== "pft" && (
                <SetupCard title={`HTML Editor - ${pageInfo.name}`}>
                  <CodeEditor
                    name="html-editor"
                    height="400px"
                    language="html"
                    value={html}
                    onChange={handleHtmlEditorChange}
                    EditorActions={CodeEditorActionButtons}
                  />
                </SetupCard>
              )
            }
            {
              pageType !== "custom" && (
                <SetupCard title="CSS Editor">
                  <CodeEditor
                    name="css-editor"
                    height="400px"
                    language="css"
                    value={css}
                    onChange={handleCssEditorChange}
                    EditorActions={CodeEditorActionButtons}
                  />
                </SetupCard>
              )
            }
            {
              pageType !== "custom" && (
                <SetupCard title="JavaScript Editor">
                  <CodeEditor
                    name="js-editor"
                    value={js}
                    height="400px"
                    onChange={handleJsEditorChange}
                    language="javascript"
                    EditorActions={CodeEditorActionButtons}
                  />
                </SetupCard>
              )
            }
            {
              pageType !== "custom" && (
                <Box data-testid="pages-page-settings-dropzone-content">
                  <PageSettingsResources
                    resources={pageResources}
                    onAddResource={onAddDocumentResource}
                    onRemoveResource={onRemoveDocumentResource}
                    onRemoveAllResource={onRemoveAllDocumentResource}
                    onDownloadResources={onDownloadPageResources}
                    accept={fileFormats}
                    isResourceDownloading={isResourceDownloading}
                  />
                </Box>
              )
            }
          </Box>
          <Box>
            {["template", "pft"].includes(pageType) && (
              <Box data-testid="pages-page-settings">
                <PageModifiers
                  pageModifiers={modifiersToRender}
                  defaultPageModifiersName={defaultPageModifiersName}
                  errors={errors.toJS()}
                  onSetModifierByName={onSetModifierByName}
                  onRemoveModifier={pageType === "template" ? onRemoveModifier : undefined}
                />
                {["template"].includes(pageType) && (
                  <Box component={Card} px={3} py={2} data-testid="pages-page-add-modifier">
                    <ModifierFormContainer />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Toolbar />
      </Loader>
    </Container>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(PageSettingsContainer);
